import {
  Button as MuiButton,
  CircularProgress,
  Grid,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'

import { extractGaDataProps } from '../../utils/analytics'
import styles from './styles'

const useStyles = makeStyles(styles)

/**
 * @typedef {import('@material-ui/core').ButtonProps} MuiButtonProps
 * @typedef {MuiButtonProps & {
 *   color: ButtonColor;
 *   loading?: boolean;
 * }} ButtonProps
 */

/**
 * ```
 * import { Button } from '@pickupp/ui/core'
 * ```
 *
 * https://material-ui.com/api/button/
 *
 * @param {ButtonProps} props
 */
const Button = ({
  color = 'primary',
  variant = 'contained',
  size = 'medium',
  loading = false,
  disabled = false,
  childProps = {},
  children,
  ...muiProps
}) => {
  const classes = useStyles({
    color,
    variant,
    loading,
  })

  // Get data props
  const [dataProps, otherProps] = extractGaDataProps(muiProps, { camelCased: true })

  // The <span> of "MuiButton-label" cannot get the 'data-' props, causing it not to send the ga event
  // So send ga event within onClick of MuiButton instead
  const handleClick = (e) => {
    // Send ga event
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({
      event: 'gtm.click',
      'gtm.element': { dataset: dataProps },
    })

    if (muiProps.onClick) {
      muiProps.onClick(e)
    }
  }

  return (
    <MuiButton
      size={size}
      variant={variant}
      disabled={disabled}
      classes={{
        root: classes.root,
        label: classes.label,
      }}
      {...otherProps} // Prevent sending the same ga event again
      onClick={handleClick}
    >
      {loading && (
        <Grid
          container
          direction="row"
          justify="center"
          alignItems="center"
          className={classes.loadingContainer}
        >
          <CircularProgress
            className={classes.loading}
            size="1.5rem"
          />
        </Grid>
      )}
      <span
        className={classes.childrenWrap}
        {...childProps}
      >
        {children}
      </span>
    </MuiButton>
  )
}

export default Button
